import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { SoftDataGrid } from "../../../components/data/SoftDataGrid";
import { getVoucher, getVouchers, revokeVoucher } from "../../../services/vouchersService";
import { getMonthToDateRange } from "../../../utils/localDate";
import { getApiErrorMessage } from "../../../utils/apiData";

function paginatorFrom(payload) {
  return payload?.vouchers ?? payload?.data?.vouchers ?? payload?.data ?? payload ?? {};
}

export function VouchersPage() {
  const queryClient = useQueryClient();
  const [range, setRange] = useState(getMonthToDateRange);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const query = useQuery({
    queryKey: ["vouchers", range, search, status, paginationModel],
    queryFn: () =>
      getVouchers({
        ...range,
        search: search || undefined,
        status: status || undefined,
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
      }),
    refetchInterval: 60000,
  });

  const paginator = paginatorFrom(query.data);
  const rows = Array.isArray(paginator?.data) ? paginator.data : [];

  const detailQuery = useQuery({
    queryKey: ["voucher", selected?.id],
    queryFn: () => getVoucher(selected.id),
    enabled: Boolean(selected?.id),
  });
  const voucher = detailQuery.data?.voucher ?? selected;

  const revokeMutation = useMutation({
    mutationFn: (id) => revokeVoucher(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["vouchers"] });
      queryClient.invalidateQueries({ queryKey: ["activity"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-overview"] });
      setSelected(null);
      setMessage({ title: "Voucher revoked", text: response?.message || "Voucher revoked successfully." });
    },
    onError: (error) => setMessage({ title: "Unable to revoke", text: getApiErrorMessage(error) }),
  });

  const columns = useMemo(
    () => [
      { field: "code", headerName: "Voucher code", minWidth: 205, flex: 1 },
      { field: "shop", headerName: "Shop", minWidth: 190, valueGetter: (_, row) => row.shop?.shop_name || "—" },
      { field: "package", headerName: "Package", minWidth: 165, valueGetter: (_, row) => row.wifi_package?.name || "—" },
      { field: "receipt", headerName: "Receipt", minWidth: 140, valueGetter: (_, row) => row.voucher_request?.receipt_number || "—" },
      { field: "currency", headerName: "Currency", width: 90 },
      { field: "amount", headerName: "Amount", width: 100 },
      {
        field: "status",
        headerName: "Status",
        width: 105,
        renderCell: (params) => <Chip size="small" label={params.row.status} sx={{ borderRadius: 0 }} />,
      },
      { field: "issued_at", headerName: "Issued", minWidth: 165 },
      {
        field: "actions",
        headerName: "",
        width: 55,
        sortable: false,
        renderCell: (params) => (
          <IconButton size="small" onClick={() => setSelected(params.row)}>
            <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
          </IconButton>
        ),
      },
    ],
    [],
  );

  return (
    <Box sx={{ p: { xs: 1.2, md: 2 }, bgcolor: "#F8FAFC", minHeight: "100%" }}>
      <Paper variant="outlined" sx={{ borderRadius: 0 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ p: 1.2, borderBottom: "1px solid #E3E7EC" }}>
          <TextField
            size="small"
            type="date"
            label="From"
            value={range.from_date}
            onChange={(event) => setRange((current) => ({ ...current, from_date: event.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={range.to_date}
            onChange={(event) => setRange((current) => ({ ...current, to_date: event.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search code, receipt or shop"
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
            sx={{ minWidth: 280 }}
          />
          <TextField select size="small" label="Status" value={status} onChange={(event) => setStatus(event.target.value)} sx={{ minWidth: 140 }}>
            <MenuItem value="">All statuses</MenuItem>
            <MenuItem value="unused">Unused</MenuItem>
            <MenuItem value="used">Used</MenuItem>
            <MenuItem value="revoked">Revoked</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
          </TextField>
        </Stack>

        <SoftDataGrid
          rows={rows}
          columns={columns}
          loading={query.isLoading}
          rowCount={paginator?.meta?.total ?? paginator?.total ?? rows.length}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50, 100]}
          getRowId={(row) => row.id}
        />
      </Paper>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle>Voucher details</DialogTitle>
        <DialogContent dividers>
          {voucher ? (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
              {[
                ["Code", voucher.code],
                ["Status", voucher.status],
                ["Shop", voucher.shop?.shop_name],
                ["Package", voucher.wifi_package?.name],
                ["Receipt", voucher.voucher_request?.receipt_number],
                ["Amount", `${voucher.currency} ${voucher.amount}`],
                ["Issued", voucher.issued_at],
                ["Expires", voucher.expires_at],
                ["First used", voucher.first_used_at || "—"],
                ["Last used", voucher.last_used_at || "—"],
                ["RADIUS synced", voucher.radius_synced_at || "—"],
                ["Revoked", voucher.revoked_at || "—"],
              ].map(([label, value]) => (
                <Paper key={label} variant="outlined" sx={{ p: 1, borderRadius: 0 }}>
                  <Typography sx={{ fontSize: "0.62rem", color: "#667085" }}>{label}</Typography>
                  <Typography sx={{ mt: 0.2, fontSize: "0.72rem", fontWeight: 750 }}>{value || "—"}</Typography>
                </Paper>
              ))}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setSelected(null)}>Close</Button>
          {voucher?.status !== "revoked" ? (
            <Button
              color="error"
              variant="contained"
              startIcon={<BlockRoundedIcon />}
              disabled={revokeMutation.isPending}
              onClick={() => revokeMutation.mutate(voucher.id)}
            >
              {revokeMutation.isPending ? "Revoking..." : "Revoke voucher"}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(message)} onClose={() => setMessage(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{message?.title}</DialogTitle>
        <DialogContent dividers><Typography>{message?.text}</Typography></DialogContent>
        <DialogActions><Button onClick={() => setMessage(null)}>OK</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
